import * as React from 'react';
import Collapse from '@mui/material/Collapse';
import IconButton from '@mui/material/IconButton';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { Grid, TablePagination } from '@mui/material';
import { t } from 'i18next';
import BakeasyLinkButton from '../buttons/BakeasyLinkButton';
import { BakeasyButton } from '../buttons/BakeasyButton';
import { GRAY_200 } from '../../constants/Colors';
import { TablePaginationActions } from './BakeasyTable';
import { getCurrency } from '../../utils/commonUtils';
import { useAppSelector } from '../../redux_store/reduxStore';
import { getProfileState } from '../../redux_store/slices/GetUserProfileSlice';
import i18n from '../../languages/languages';

interface Data {
  productName: string;
  category: string;
  subCategory: string;
  storeName: string;
  name: string;
  store: string;
  totalOrder: number;
  history: [];
  orderId: string;
  storeNumber: number;
  date: string;
  status: string;
  actions: JSX.Element[];
  edit: JSX.Element[];
  totalPrice: number;
}

export interface CollapsibleTableHeadCell {
  id: keyof Data | string;
  label: string;
  disablePadding?: Boolean;
  isChip?: boolean | false;
  shippingMemo?: boolean | false;
  width?: any;
  numeric?: boolean | false;
  align?: string | 'left';
}

interface BakeasyCollapsibleTableProps {
  rowHistoryData?: any;
  reportTableHeaders: CollapsibleTableHeadCell[];
  tableHeaders: CollapsibleTableHeadCell[];
  rowsData: any;
  count?: any;
  refreshPageData?: any;
  filter?: any;
  pageNo?: number;
  menu?: string;
  search?: any;
}

interface BakeasyCollapsibleTableHeadProps {
  reportTableHeaders?: CollapsibleTableHeadCell[];
  tableHeaders?: CollapsibleTableHeadCell[];
  rowHistoryData?: any;
}

function Row(props: any) {
  const { row, headCells, menu } = props;
  const [open, setOpen] = React.useState(false);
  const [selected, setSelected] = React.useState<readonly number[]>([]);
  const isSelected = (id: number) => selected.indexOf(id) !== -1;

  function CustomTableCell(id: any, row: any) {
    switch (id) {
      case 'name':
        if (row['onNameClick']) {
          return (
            <BakeasyLinkButton
              label={row[id]}
              style={{ fontWeight: 'bold' }}
              onClick={row['onNameClick']}
            />
          );
        } else {
          return row[id];
        }
      case 'contact':
        if (row['onContactClick']) {
          return <BakeasyLinkButton label={row[id]} onClick={row['onContactClick']} />;
        } else {
          return row[id];
        }
      case 'products':
        if (row['onProductsClick']) {
          return <BakeasyLinkButton label={row[id]} onClick={row['onProductsClick']} />;
        } else {
          return row[id];
        }
      case 'invoiceId':
        if (row['onInvoiceIdClick']) {
          return <BakeasyLinkButton label={row[id]} onClick={row['onInvoiceIdClick']} />;
        } else {
          return row[id];
        }
      case 'shippedDate':
        if (row['onShippedMemoClick']) {
          return (
            <>
              <Typography>{row[id]}</Typography>
              <BakeasyLinkButton
                label={t('SHIPPING_MEMO')}
                onClick={row['onShippedMemoClick']}
                style={{ fontSize: '0.9rem', fontWeight: 'bold', textAlign: 'left' }}
              />
            </>
          );
        } else {
          return row[id];
        }
      case 'icon':
        if (row['onIconClick']) {
          if (row['isSubOrderPresent'] === undefined || row['isSubOrderPresent'] === true) {
            return (
              <IconButton
                onClick={event => {
                  if (row['isSubOrderPresent'] === undefined || row['isSubOrderPresent'] === true) {
                    setOpen(!open);
                  }
                  row['onIconClick']();
                }}
              >
                {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
              </IconButton>
            );
          } else {
            return (
              <IconButton disabled>
                <KeyboardArrowDownIcon />
              </IconButton>
            );
          }
        } else {
          return row[id];
        }
      case 'measureType':
        return row[id][i18n.language] || row[id]['measureType'][i18n.language];
      default:
        return row[id];
    }
  }

  function CustomTableCellHistory(id: any, historyRow: any) {
    switch (id) {
      case 'invoiceId':
        if (historyRow['onInvoiceIdClick']) {
          return (
            <BakeasyLinkButton label={historyRow[id]} onClick={historyRow['onInvoiceIdClick']} />
          );
        } else {
          return historyRow[id];
        }
      case 'recipe':
        if (historyRow['onRecipeClick']) {
          return (
            <BakeasyButton
              size="small"
              label={historyRow[id]}
              onClick={historyRow['onRecipeClick']}
            />
          );
        } else {
          return historyRow[id];
        }
      case 'shippedDate':
        if (historyRow['onShippedMemoClick']) {
          return (
            <>
              <Typography>{historyRow[id]}</Typography>
              <BakeasyLinkButton
                label={t('SHIPPING_MEMO')}
                onClick={historyRow['onShippedMemoClick']}
                style={{ fontSize: '0.9rem', fontWeight: 'bold', textAlign: 'left' }}
              />
            </>
          );
        } else {
          return row[id];
        }
      case 'subOrderId':
        if (historyRow['onSubOrderIdClick']) {
          return (
            <BakeasyLinkButton label={historyRow[id]} onClick={historyRow['onSubOrderIdClick']} />
          );
        } else {
          return historyRow[id];
        }
      default:
        return historyRow[id];
    }
  }

  return (
    <React.Fragment key={row.key}>
      <TableRow hover sx={{ cursor: 'pointer' }}>
        {headCells?.map((header: any, headerIndex: number) => (
          <TableCell
            key={headerIndex}
            style={{
              padding: 10,
              width: header.width ? header.width : 'auto',
              textAlign: header?.numeric ? 'right' : header.align,
              maxWidth: '400px'
            }}
          >
            {CustomTableCell(header.id, row)}
          </TableCell>
        ))}
      </TableRow>
      <TableRow>
        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={9}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Grid sx={{ margin: 1 }} justifyContent={'center'}>
              <Grid>
                <Table size="small" aria-label="purchases" sx={{ marginLeft: 5 }}>
                  <BakeasyCollapsibleTableHead tableHeaders={props?.tableHeaders} />
                  <TableBody>
                    {row?.history?.map((historyRow: any, index: number) => {
                      const isItemSelected = isSelected(row.id);
                      return (
                        <TableRow
                          key={index}
                          hover
                          sx={{ cursor: 'pointer' }}
                          aria-checked={isItemSelected}
                          tabIndex={-1}
                          selected={isItemSelected}
                        >
                          {props?.tableHeaders?.map((header: any, index: number) => {
                            return (
                              <TableCell
                                key={index}
                                style={{
                                  padding: 10,
                                  width: header.width ? header.width : 'auto',
                                  textAlign: header?.numeric ? 'right' : header.align
                                  //maxWidth: '400px'
                                }}
                              >
                                {CustomTableCellHistory(header.id, historyRow)}
                              </TableCell>
                            );
                          })}
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </Grid>
            </Grid>
          </Collapse>
        </TableCell>
      </TableRow>
    </React.Fragment>
  );
}

function BakeasyCollapsibleTableHead(props: BakeasyCollapsibleTableHeadProps) {
  let _userProfile = useAppSelector(getProfileState);

  function getHeaderLabel(headCell: any) {
    switch (headCell?.id) {
      case 'invoicedValue':
        return t(headCell?.label) + getCurrency(_userProfile?.value);
      default:
        return t(headCell?.label);
    }
  }
  return (
    <TableHead>
      <TableRow>
        {props?.reportTableHeaders &&
          props?.reportTableHeaders?.map((headCell: any, index: number) => (
            <TableCell
              style={{
                padding: 10,
                backgroundColor: GRAY_200,
                width: headCell.width ? headCell.width : 'auto',
                textAlign: headCell?.numeric ? 'right' : headCell.align,
                maxWidth: '400px'
              }}
              key={index}
              // key={headCell.id}
            >
              {/* <Typography style={{ fontWeight: 600 }}>{t(headCell.label)}</Typography> */}
              <Typography style={{ fontWeight: 'bold' }}>{getHeaderLabel(headCell)}</Typography>
            </TableCell>
          ))}
        {props?.tableHeaders &&
          props?.tableHeaders?.map((headCell: any, index: number) => (
            <TableCell
              key={index}
              style={{
                padding: 10,
                backgroundColor: GRAY_200,
                width: headCell.width ? headCell.width : 'auto',
                textAlign: headCell?.numeric ? 'right' : headCell.align,
                maxWidth: '400px'
              }}
            >
              <Typography style={{ fontWeight: 600 }}>{t(headCell.label)}</Typography>
            </TableCell>
          ))}
      </TableRow>
    </TableHead>
  );
}

export default function BakeasyCollapsibleTable(props: BakeasyCollapsibleTableProps) {
  const { rowsData, count, filter, pageNo, menu, refreshPageData, search } = props;

  const [page, setPage] = React.useState(pageNo ?? 0);
  const [order, setOrder] = React.useState<number>(filter?.sortDirection ?? -1);
  const [orderBy, setOrderBy] = React.useState<string>(filter?.sortBy ?? '');

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
    refreshPageData(newPage, search, filter);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setPage(0);
    let newFilter: any = {
      ...filter,
      limit: Number(event.target.value)
    };

    refreshPageData(0, search, newFilter);
  };

  return (
    <>
      <TableContainer component={Paper} style={{ overflowX: 'auto' }}>
        <Table stickyHeader sx={{ minWidth: 500 }} aria-labelledby="tableTitle">
          <BakeasyCollapsibleTableHead reportTableHeaders={props.reportTableHeaders} />
          <TableBody>
            {rowsData.map((row: any, index: number) => (
              <Row
                key={index}
                row={row}
                headCells={props.reportTableHeaders}
                tableHeaders={props.tableHeaders}
                isChip={true}
                menu={menu}
              />
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      {menu === 'invoices' && (
        <TablePagination
          rowsPerPageOptions={[10, 25, 50]}
          component="div"
          count={count}
          rowsPerPage={filter?.limit}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          ActionsComponent={TablePaginationActions}
          labelRowsPerPage={t('ROWS_PER_PAGE')}
        />
      )}
    </>
  );
}
